import { computed } from 'vue';
import { useNexceoVoiceStore } from 'dashboard/stores/nexceoVoice';
import NexceoVoiceAPI from 'dashboard/api/nexceo/voice';
import { useAlert } from 'dashboard/composables';

// M29.2 Voice Experience — the single composable every Voice UI component
// uses. Wraps Nexceo::Voice's minimal REST surface + the Pinia store;
// components never call NexceoVoiceAPI or useNexceoVoiceStore directly, so
// error handling and payload normalization live in exactly one place.
export function useNexceoVoice() {
  const store = useNexceoVoiceStore();

  const activeCall = computed(() => store.activeCall);
  const ringingCalls = computed(() => store.ringingCalls);
  const incomingCalls = computed(() => store.incomingRingingCalls);
  const missedCalls = computed(() => store.missedCalls);
  const notifications = computed(() => store.notifications);
  const connectionStatus = computed(() => store.connectionStatus);
  const hasActiveCall = computed(() => store.hasActiveCall);

  const callForConversation = conversationId =>
    store.callForConversation(conversationId);

  const fetchCapabilities = async conversationId => {
    try {
      return await NexceoVoiceAPI.getCapabilities(conversationId);
    } catch (error) {
      return { provider: null, capabilities: null };
    }
  };

  // M29.2.1 — a 409 from the race-condition guard (Nexceo::Voice::
  // SessionsController#with_expected_state) means another request (this
  // tab, another tab, another agent) already won: `error.response.data`
  // is `{ error: 'already_handled', state: <actual state> }`, not a real
  // failure. Reconcile the store to that actual state immediately instead
  // of waiting for a broadcast that may already have been sent before this
  // tab's own subscription caught up, and skip the alert — losing a race
  // isn't something the user did wrong.
  const isAlreadyHandledConflict = error => error?.response?.status === 409;

  const reconcileConflict = (callId, error) => {
    const state = error?.response?.data?.state;
    if (state) store.upsertCall({ call_id: callId, state });
  };

  const handleError = (error, fallbackMessage, callId = null) => {
    if (isAlreadyHandledConflict(error)) {
      if (callId) reconcileConflict(callId, error);
      return;
    }
    useAlert(error?.response?.data?.error || fallbackMessage);
  };

  const startCall = async ({ conversationId, provider, to }) => {
    try {
      const session = await NexceoVoiceAPI.startCall({
        conversationId,
        provider,
        to,
      });
      store.upsertCall({ ...session, direction: 'outgoing', conversationId });
      return session;
    } catch (error) {
      handleError(error, 'Unable to start call');
      return null;
    }
  };

  const acceptCall = async callId => {
    try {
      const session = await NexceoVoiceAPI.acceptCall(callId);
      store.upsertCall(session);
      return session;
    } catch (error) {
      handleError(error, 'Unable to accept call', callId);
      return null;
    }
  };

  const rejectCall = async callId => {
    try {
      const session = await NexceoVoiceAPI.rejectCall(callId);
      store.upsertCall(session);
      return session;
    } catch (error) {
      handleError(error, 'Unable to reject call', callId);
      return null;
    }
  };

  const endCall = async callId => {
    try {
      const session = await NexceoVoiceAPI.endCall(callId);
      store.upsertCall(session);
      return session;
    } catch (error) {
      handleError(error, 'Unable to end call', callId);
      return null;
    }
  };

  const dismissNotification = id => store.dismissNotification(id);

  // M29.2.1 Voice Reliability — the rehydration entry point. Called on
  // mount (covers a browser refresh — a fresh page load remounts
  // everything from scratch, so there is no other hook a refresh would go
  // through) and on every ActionCable reconnect (covers a dropped
  // websocket / browser sleep-wake / network blip, where any voice.*
  // broadcasts sent while disconnected are otherwise lost forever — see
  // NexceoVoiceListener.vue). Deliberately silent on failure — this runs
  // automatically and often (every reconnect); an alert every time the
  // network hiccups would be worse than the missed-state risk it's guarding against.
  const rehydrate = async () => {
    try {
      const sessions = await NexceoVoiceAPI.listActiveSessions();
      store.rehydrate(sessions);
    } catch (error) {
      // Intentionally no-op — see comment above.
    }
  };

  return {
    activeCall,
    ringingCalls,
    incomingCalls,
    missedCalls,
    notifications,
    connectionStatus,
    hasActiveCall,
    callForConversation,
    fetchCapabilities,
    startCall,
    acceptCall,
    rejectCall,
    endCall,
    dismissNotification,
    rehydrate,
  };
}
